import { CalendarIcon } from '@diligentcorp/atlas-react-icons';
import {
  useDatePickerKeyboard,
  useDateTimeFormat,
} from '@issuemanager/apps/client/hooks';
import { Stack, useEventCallback } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import type { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import dayjs from 'dayjs';
import { MouseEvent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDataGridContext } from '../data-grid/data-grid-context';
import type { ExtendedGridColumnHeaderProps } from '../data-grid/types';
import { TextField } from '../inputs';
import { FilterDropdown, FooterWithSlot } from './components';
import { useFilterTexts, useHandlePopper } from './hooks';

export interface DateFilterProps {
  onChange: (range: DateRange<Date>) => void;
  value: DateRange<Date>;
}

export function DateFilter({ onChange, value }: DateFilterProps) {
  const {
    handleOpen: handlePopperOpen,
    setOpenPopper,
    setAnchorEl,
    openPopper,
    anchorEl,
  } = useHandlePopper();
  const { date: dateFormat, timezoneOffset } = useDateTimeFormat();

  const [range, setRange] = useState<DateRange<Date>>(() => {
    if (!value || !Array.isArray(value)) {
      return [null, null];
    }
    return [value[0] ?? null, value[1] ?? null];
  });

  const [isFromPickerOpen, setIsFromPickerOpen] = useState(false);
  const [isToPickerOpen, setIsToPickerOpen] = useState(false);

  const openPickerButtonFromRef = useRef<HTMLButtonElement | null>(null);
  const openPickerButtonToRef = useRef<HTMLButtonElement | null>(null);

  useDatePickerKeyboard({
    isPickerOpen: isFromPickerOpen,
    onClose: () => setIsFromPickerOpen(false),
  });

  useDatePickerKeyboard({
    isPickerOpen: isToPickerOpen,
    onClose: () => setIsToPickerOpen(false),
  });

  const handleInputClickFrom = (
    e: MouseEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    e.stopPropagation();
    openPickerButtonFromRef.current?.click();
  };

  const handleInputClickTo = (
    e: MouseEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    e.stopPropagation();
    openPickerButtonToRef.current?.click();
  };

  const { t } = useTranslation(['common', 'issue']);

  const { dropdownPlaceholderText } = useFilterTexts({
    range: {
      value: range,
    },
  });

  const handleClear = (): void => {
    setRange([null, null]);
    onChange([null, null]);
  };

  const handleClose = useCallback(() => {
    if (!value || !Array.isArray(value)) {
      setRange([null, null]);
    } else {
      setRange([value[0] ?? null, value[1] ?? null]);
    }
    if (openPopper && anchorEl) {
      setAnchorEl(null);
      setOpenPopper(false);
    }
  }, [value, openPopper, anchorEl, setAnchorEl, setOpenPopper]);

  const handleApplySelection = useCallback(() => {
    setOpenPopper(false);
    const convertedRange: DateRange<Date> = [
      range[0]
        ? dayjs(range[0]).utc().utcOffset(timezoneOffset, true).toDate()
        : null,
      range[1]
        ? dayjs(range[1]).utc().utcOffset(timezoneOffset, true).toDate()
        : null,
    ];
    onChange(convertedRange);
  }, [range, timezoneOffset, onChange, setOpenPopper]);

  const handleFromPickerOpen = (): void => {
    if (isToPickerOpen) {
      setIsToPickerOpen(false);
    }
    setIsFromPickerOpen(true);
    openPickerButtonFromRef.current?.focus();
  };

  const handleToPickerOpen = (): void => {
    if (isFromPickerOpen) {
      setIsFromPickerOpen(false);
    }
    setIsToPickerOpen(true);
    openPickerButtonToRef.current?.focus();
  };

  const isFromValid = !range[0] || dayjs(range[0]).isValid();
  const isToValid = !range[1] || dayjs(range[1]).isValid();
  const isApplyDisabled = !isFromValid || !isToValid;

  return (
    <FilterDropdown
      onClick={handlePopperOpen}
      placeholder={dropdownPlaceholderText}
      openPopper={openPopper}
      anchorEl={anchorEl}
      onClose={handleClose}
    >
      <Stack spacing={1} padding={1}>
        <Stack direction='row' spacing={1}>
          <DateTimePicker
            inputFormat={`${dateFormat} hh:mm A`}
            value={range[0] ?? null}
            open={isFromPickerOpen}
            onChange={(value) => {
              setRange((prevState) => [value, prevState[1]]);
            }}
            onOpen={handleFromPickerOpen}
            onClose={() => {
              setIsFromPickerOpen(false);
            }}
            OpenPickerButtonProps={{
              ref: (ref) => {
                openPickerButtonFromRef.current = ref;
              },
            }}
            components={{ OpenPickerIcon: CalendarIcon }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('common:filters.date.from')}
                error={Boolean(params.error)}
                inputProps={{
                  ...params.inputProps,
                  onClick: handleInputClickFrom,
                  tabIndex: -1,
                  onKeyDown: (e) => {
                    e.preventDefault();
                  },
                }}
              />
            )}
          />
          <DateTimePicker
            inputFormat={`${dateFormat} hh:mm A`}
            value={range[1] ?? null}
            open={isToPickerOpen}
            onChange={(value) => {
              setRange((prevState) => [prevState[0], value]);
            }}
            onOpen={handleToPickerOpen}
            onClose={() => {
              setIsToPickerOpen(false);
            }}
            OpenPickerButtonProps={{
              ref: (ref) => {
                openPickerButtonToRef.current = ref;
              },
            }}
            components={{ OpenPickerIcon: CalendarIcon }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('common:filters.date.to')}
                error={Boolean(params.error)}
                inputProps={{
                  ...params.inputProps,
                  onClick: handleInputClickTo,
                  tabIndex: -1,
                  onKeyDown: (e) => {
                    e.preventDefault();
                  },
                }}
              />
            )}
          />
        </Stack>
      </Stack>
      <FooterWithSlot
        clear={{
          props: {
            onClick: handleClear,
          },
        }}
        cancel={{
          props: {
            onClick: handleClose,
          },
        }}
        apply={{
          props: {
            onClick: handleApplySelection,
            disabled: isApplyDisabled,
          },
        }}
      />
    </FilterDropdown>
  );
}

export function DataGridDateFilter(params: ExtendedGridColumnHeaderProps) {
  const context = useDataGridContext();

  const handleChange = useEventCallback((value: DateRange<Date>) => {
    context.setFilterModel({
      ...context.filterModel,
      [params.field]: value,
    });
  });

  return (
    <DateFilter
      onChange={handleChange}
      value={context.filterModel[params.field] as DateRange<Date>}
    />
  );
}
